import type { ImportAddressFix, ImportRowError } from "@adva/types";

export function formatNormalizedAddress(fix: ImportAddressFix): string {
  const parts = [
    fix.normalized_street,
    [fix.normalized_city, fix.normalized_state].filter(Boolean).join(", "),
    fix.normalized_zip,
  ].filter((p) => typeof p === "string" && p.length > 0);
  return parts.length > 0 ? parts.join(", ") : "—";
}

export function precisionLabel(precision: string | null): string {
  if (!precision) return "Unknown";
  return precision.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function confidenceTone(
  confidence: number,
): "success" | "warning" | "neutral" {
  if (confidence >= 0.95) return "success";
  if (confidence >= 0.85) return "warning";
  return "neutral";
}

export const HIGH_CONFIDENCE_THRESHOLD = 0.9;

// Client-side CSV export of import row errors. The Errors tab download is
// a client-side operation: errors[] already lives on the import-job detail,
// so a Blob conversion + anchor click is enough — no API roundtrip needed.
export function buildErrorsCsv(errors: ImportRowError[]): string {
  const headers = ["row", "external_id", "field", "message", "value"];
  const escape = (v: unknown): string => {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [headers.join(",")];
  for (const e of errors) {
    lines.push(
      [
        escape(e.row),
        escape(e.external_id),
        escape(e.field),
        escape(e.message),
        escape(e.value),
      ].join(","),
    );
  }
  return lines.join("\n");
}

export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function pluralize(
  n: number,
  singular: string,
  plural?: string,
): string {
  return `${n.toLocaleString()} ${n === 1 ? singular : (plural ?? singular + "s")}`;
}
